import { useState } from "react";
import { Sparkles, ChevronDown, ChevronUp, Share2, Loader2, Check } from "lucide-react";
import { generateBreakdownCard, downloadBlob } from "@/lib/breakdownCard";
import { api } from "@/lib/api";

/**
 * <WhyMatched/> — explainer panel for a Discover card.
 *
 * Takes the `breakdown` object the backend now returns on each /api/discover
 * result and translates the per-trait similarities into a human-readable
 * "Why we matched you" list. Doppel highlights similarities, Chaos highlights
 * which traits *contrast* the most — both surfaces the same data, framed for
 * the active mode.
 *
 * Props:
 *   breakdown — { embedding_sim, geometry_sim, traits: { eyes, nose, ... } } | null
 *   mode      — "doppel" | "chaos"
 *   compact   — render the collapsible inline-on-card variant when true
 *   viewer    — { photo_url, name } of the signed-in user (used for share card)
 *   match     — { photo_url, name } of the candidate (used for share card)
 */
const TRAIT_LABELS = {
  eyes: "Eye spacing & shape",
  nose: "Nose proportions",
  mouth: "Mouth & lips",
  jaw: "Jawline",
  cheek_chin: "Cheekbones & chin",
  proportions: "Face proportions",
  symmetry: "Symmetry",
};

const DOPPEL_COPY = {
  high: "Strong match",
  mid: "Pretty close",
  low: "Different",
};
const CHAOS_COPY = {
  high: "Total contrast",
  mid: "Plot twist",
  low: "Similar",
};

const labelFor = (sim, mode) => {
  const set = mode === "chaos" ? CHAOS_COPY : DOPPEL_COPY;
  // Chaos cares about CONTRAST → higher = lower geom_sim → invert the bands
  const v = mode === "chaos" ? 1 - sim : sim;
  if (v >= 0.7) return set.high;
  if (v >= 0.4) return set.mid;
  return set.low;
};

const barColorFor = (sim, mode) => {
  const v = mode === "chaos" ? 1 - sim : sim;
  if (v >= 0.7) return "bg-gradient-to-r from-pink-500 to-orange-400";
  if (v >= 0.4) return "bg-gradient-to-r from-pink-300 to-pink-400";
  return "bg-slate-200";
};

export default function WhyMatched({ breakdown, mode = "doppel", compact = true, viewer = null, match = null }) {
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [sharedAt, setSharedAt] = useState(0);
  if (!breakdown) return null;
  const traits = breakdown.traits || null;
  const geomSim = breakdown.geometry_sim;
  // Sort traits so the most relevant ones (highest for doppel, lowest for
  // chaos) bubble to the top.
  const entries = traits
    ? Object.entries(traits)
        .filter(([k]) => TRAIT_LABELS[k])
        .sort((a, b) => (mode === "chaos" ? a[1] - b[1] : b[1] - a[1]))
    : [];

  const headline = (() => {
    if (mode === "chaos") {
      if (geomSim != null && geomSim < 0.35) return "Different face shape — same vibe";
      return "Plot twist match";
    }
    if (geomSim != null && geomSim >= 0.7) return "Anatomical twin energy";
    if (geomSim != null && geomSim >= 0.5) return "Strong bone-structure match";
    return "Twin energy";
  })();

  const onShare = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      // Mystery-by-default: only show the match's face if they've explicitly
      // opted in. The CTA below directs viewers to the reveal landing page
      // where they can sign up and try their own match.
      const matchOptIn = Boolean(match?.share_opt_in);
      const revealUrl = viewer?.id && match?.id
        ? `${window.location.origin}/reveal/${viewer.id}/${match.id}`
        : `${window.location.origin}/`;
      const { blob } = await generateBreakdownCard({
        viewerPhoto: viewer?.photo_url || "",
        matchPhoto: matchOptIn ? (match?.photo_url || "") : "",
        matchName: match?.name || "",
        breakdown,
        mode,
        url: revealUrl,
        matchOptIn,
      });
      // Prefer native share with the image; fall back to download.
      const filename = `doppelcrush-${mode === "chaos" ? "chaos" : "twin"}.png`;
      const file = blob ? new File([blob], filename, { type: "image/png" }) : null;
      if (file && navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: "DoppelCrush — Why we matched",
            url: revealUrl,
            text: mode === "chaos"
              ? "Total plot-twist match on DoppelCrush 💀 tap to see who"
              : "Found my DoppelCrush 👯 tap to see who",
          });
          api.post("/share", { kind: "match_card" })
            .catch((err) => console.warn("share log failed", err));
        } catch { /* user cancelled */ }
      } else if (blob) {
        downloadBlob(blob, filename);
        api.post("/share", { kind: "match_card" })
          .catch((err) => console.warn("share log failed", err));
      }
      setSharedAt(Date.now());
      setTimeout(() => setSharedAt(0), 2200);
    } catch (err) {
      // Surface so we don't fail silently in prod telemetry.
      console.warn("share-card render failed", err);
    } finally {
      setSharing(false);
    }
  };

  return (
    <div
      className={`mt-2 rounded-2xl border-2 ${
        mode === "chaos"
          ? "border-orange-100 bg-orange-50/80"
          : "border-pink-100 bg-pink-50/80"
      } backdrop-blur-sm`}
      data-testid="why-matched"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-3 py-2 flex items-center justify-between gap-2 text-left"
        data-testid="why-matched-toggle"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-1.5 font-display font-bold text-[12px] text-slate-900">
          <Sparkles className="w-3.5 h-3.5 text-pink-500" /> Why we matched —{" "}
          <span className={mode === "chaos" ? "text-orange-600" : "text-pink-600"}>
            {headline}
          </span>
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-slate-500 shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" />
        )}
      </button>

      {open ? (
        <div className="px-3 pb-3 pt-1 space-y-2" data-testid="why-matched-body">
          {entries.length === 0 ? (
            <p className="text-[11px] font-body text-slate-500">
              {breakdown.embedding_sim != null ? (
                <>
                  Based on facial appearance — overall similarity{" "}
                  <b className="text-slate-700">
                    {Math.round(Math.max(0, Math.min(1, breakdown.embedding_sim)) * 100)}%
                  </b>
                  .
                </>
              ) : (
                "Based on facial appearance."
              )}
              {compact ? "" : " Re-upload a clearer selfie for a richer breakdown."}
            </p>
          ) : (
            entries.map(([key, sim]) => {
              const pct = Math.round(
                (mode === "chaos" ? 1 - sim : sim) * 100,
              );
              return (
                <div key={key} className="text-[11px]" data-testid={`trait-${key}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold text-slate-700">
                      {TRAIT_LABELS[key]}
                    </span>
                    <span className="font-body text-slate-500">
                      {labelFor(sim, mode)} · {pct}%
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-white/60 overflow-hidden">
                    <div
                      className={`h-full ${barColorFor(sim, mode)} transition-[width]`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })
          )}
          {breakdown.embedding_sim != null && entries.length > 0 ? (
            <div className="pt-1 text-[10px] font-body text-slate-500 italic">
              + neural face-print similarity: {Math.round(Math.max(0, Math.min(1, breakdown.embedding_sim)) * 100)}%
            </div>
          ) : null}

          {viewer?.photo_url || match?.photo_url ? (
            <button
              type="button"
              onClick={onShare}
              disabled={sharing}
              className={`mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 font-display font-bold text-[11px] transition-colors disabled:opacity-60 ${
                mode === "chaos"
                  ? "bg-orange-500 text-white hover:bg-orange-600"
                  : "bg-pink-500 text-white hover:bg-pink-600"
              }`}
              data-testid="share-breakdown-btn"
              aria-label="Share this breakdown"
            >
              {sharing ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Rendering…</>
              ) : sharedAt ? (
                <><Check className="w-3.5 h-3.5" /> Shared</>
              ) : (
                <><Share2 className="w-3.5 h-3.5" /> Share this breakdown</>
              )}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
